interface SortState {
  column: number | null
  direction: 'asc' | 'desc' | null
}

interface ActiveFilters {
  [columnIndex: number]: string
}

type SortMethod = 'alpha' | 'numeric' | 'date' | 'disabled'
type SortMethodWithDirection = `${SortMethod}-${'asc' | 'desc'}` | 'disabled'

const hydratedTables = new WeakSet<HTMLElement>()

export function hydrateTableStripes(
  container: Document | Element = document
): void {
  const tables = container.querySelectorAll<HTMLElement>('.vssl-stripe--table')

  tables.forEach((wrapper) => {
    if (hydratedTables.has(wrapper)) return
    hydratedTables.add(wrapper)

    const tableEl = wrapper.querySelector<HTMLTableElement>('table')
    const tbodyEl = wrapper.querySelector<HTMLTableSectionElement>('tbody')
    if (!tableEl || !tbodyEl) return

    // Read configuration from data attributes
    const sortMethods: SortMethodWithDirection[] = JSON.parse(
      wrapper.dataset.sortMethods || '[]'
    )
    const filterMethods: ('enabled' | 'disabled')[] = JSON.parse(
      wrapper.dataset.filterMethods || '[]'
    )

    const sortState: SortState = { column: null, direction: null }
    const activeFilters: ActiveFilters = {}

    // Get all data rows (exclude header rows containing only th elements)
    const allRows = Array.from(
      tbodyEl.querySelectorAll<HTMLTableRowElement>('tr')
    )
    const originalRows = allRows.filter((row) => row.querySelector('td'))

    // Extract sort type from compound sort method (e.g., 'alpha-asc' -> 'alpha')
    function getSortType(sortMethod: SortMethodWithDirection): SortMethod {
      if (!sortMethod || sortMethod === 'disabled') return 'alpha'
      return sortMethod.split('-')[0] as SortMethod
    }

    // Extract direction from compound sort method (e.g., 'alpha-asc' -> 'asc')
    function getSortDirection(
      sortMethod: SortMethodWithDirection
    ): 'asc' | 'desc' {
      if (!sortMethod || sortMethod === 'disabled') return 'asc'
      const parts = sortMethod.split('-')
      return (parts[1] as 'asc' | 'desc') || 'asc'
    }

    // Compare two values using the specified sort method
    function compareValues(
      valueA: string,
      valueB: string,
      sortMethod: SortMethodWithDirection
    ): number {
      const sortType = getSortType(sortMethod)

      if (sortType === 'alpha') {
        return valueA.toLowerCase().localeCompare(valueB.toLowerCase())
      } else if (sortType === 'numeric') {
        const numA = parseFloat(valueA) || 0
        const numB = parseFloat(valueB) || 0
        return numA - numB
      } else if (sortType === 'date') {
        const dateA = new Date(valueA).getTime() || 0
        const dateB = new Date(valueB).getTime() || 0
        return dateA - dateB
      }
      return 0
    }

    // Initialize filter dropdowns with unique values
    function initializeFilters(): void {
      const filterSelects = wrapper.querySelectorAll<HTMLSelectElement>(
        '.vssl-stripe--table--filter-select'
      )

      filterSelects.forEach((select) => {
        const columnIndex = parseInt(select.dataset.column || '0')
        const uniqueValues = new Set<string>()

        // Collect unique values from all rows in this column
        originalRows.forEach((row) => {
          const cell = row.querySelector<HTMLTableCellElement>(
            `[data-column-index="${columnIndex}"]`
          )
          if (cell) {
            const value = cell.dataset.value || cell.textContent?.trim() || ''
            if (value) {
              uniqueValues.add(value)
            }
          }
        })

        // Use the column's sort method if defined, otherwise default to alpha-asc
        const sortMethod =
          sortMethods[columnIndex] && sortMethods[columnIndex] !== 'disabled'
            ? sortMethods[columnIndex]
            : 'alpha-asc'

        const direction = getSortDirection(sortMethod)

        const sortedValues = Array.from(uniqueValues).sort((a, b) => {
          const comparison = compareValues(a, b, sortMethod)
          return direction === 'desc' ? -comparison : comparison
        })

        sortedValues.forEach((value) => {
          const option = document.createElement('option')
          option.value = value
          option.textContent = value
          select.appendChild(option)
        })

        // Add change event listener
        select.addEventListener('change', (e) => {
          handleFilter(columnIndex, (e.target as HTMLSelectElement).value)
        })
      })
    }

    // Update sort icon display
    function updateSortIcons(): void {
      const sortableHeaders =
        wrapper.querySelectorAll<HTMLTableCellElement>('th.sortable')

      sortableHeaders.forEach((header) => {
        const column = parseInt(header.dataset.columnIndex || '0')
        const icon = header.querySelector<HTMLElement>(
          '.vssl-stripe--table--sort-icon'
        )

        if (!icon) return

        if (column === sortState.column) {
          if (sortState.direction === 'asc') {
            icon.innerHTML = '&uarr;'
          } else if (sortState.direction === 'desc') {
            icon.innerHTML = '&darr;'
          }
        } else {
          icon.innerHTML = '&updownarrow;'
        }
      })
    }

    // Sort the table
    function sortTable(
      columnIndex: number,
      sortMethod: SortMethodWithDirection,
      direction: 'asc' | 'desc'
    ): void {
      if (!tbodyEl) return

      const rows = originalRows.slice().sort((rowA, rowB) => {
        const cellA = rowA.querySelector<HTMLTableCellElement>(
          `[data-column-index="${columnIndex}"]`
        )
        const cellB = rowB.querySelector<HTMLTableCellElement>(
          `[data-column-index="${columnIndex}"]`
        )

        if (!cellA || !cellB) return 0

        const valueA = cellA.dataset.value || cellA.textContent?.trim() || ''
        const valueB = cellB.dataset.value || cellB.textContent?.trim() || ''

        const comparison = compareValues(valueA, valueB, sortMethod)

        return direction === 'desc' ? -comparison : comparison
      })

      // Re-append rows in sorted order
      rows.forEach((row) => tbodyEl.appendChild(row))
    }

    // Reset table to original order
    function resetTableOrder(): void {
      if (!tbodyEl) return

      const rows = originalRows.slice().sort((a, b) => {
        const indexA = parseInt(a.dataset.rowIndex || '0')
        const indexB = parseInt(b.dataset.rowIndex || '0')
        return indexA - indexB
      })
      rows.forEach((row) => tbodyEl.appendChild(row))
    }

    // Handle sorting
    function handleSort(
      columnIndex: number,
      sortMethod: SortMethodWithDirection
    ): void {
      const defaultDirection = getSortDirection(sortMethod)

      // Toggle sort direction: null -> default -> opposite -> null
      if (sortState.column === columnIndex) {
        if (sortState.direction === defaultDirection) {
          sortState.direction = defaultDirection === 'asc' ? 'desc' : 'asc'
        } else {
          sortState.direction = null
          sortState.column = null
        }
      } else {
        sortState.column = columnIndex
        sortState.direction = defaultDirection
      }

      // Update sort button icons
      updateSortIcons()

      // Apply sorting
      if (sortState.direction) {
        sortTable(columnIndex, sortMethod, sortState.direction)
      } else {
        // Reset to original order
        resetTableOrder()
      }

      // Reapply filters after sorting
      applyFilters()
    }

    // Apply all active filters
    function applyFilters(): void {
      originalRows.forEach((row) => {
        let shouldShow = true

        // Check each active filter
        for (const [columnIndex, filterValue] of Object.entries(
          activeFilters
        )) {
          const cell = row.querySelector<HTMLTableCellElement>(
            `[data-column-index="${columnIndex}"]`
          )
          if (cell) {
            const cellValue =
              cell.dataset.value || cell.textContent?.trim() || ''
            if (cellValue !== filterValue) {
              shouldShow = false
              break
            }
          }
        }

        row.style.display = shouldShow ? '' : 'none'
        row.classList.toggle('vssl-stripe--table--row--hidden', !shouldShow)
      })

      // Update alternating row classes after filtering
      updateAlternatingClasses()

      // Show/hide the clear filters button
      updateClearFiltersButton()
    }

    // Update odd/even classes on visible rows for alternating background colors
    function updateAlternatingClasses(): void {
      if (!tbodyEl) return

      const visibleRows = Array.from(
        tbodyEl.querySelectorAll<HTMLTableRowElement>('tr')
      ).filter((row) => row.style.display !== 'none')

      visibleRows.forEach((row, index) => {
        row.classList.remove('odd', 'even')
        row.classList.add(index % 2 === 0 ? 'odd' : 'even')
      })
    }

    // Handle filtering
    function handleFilter(columnIndex: number, filterValue: string): void {
      if (filterValue === '') {
        delete activeFilters[columnIndex]
      } else {
        activeFilters[columnIndex] = filterValue
      }

      applyFilters()
    }

    // Attach event listeners to sort buttons
    const sortButtons = wrapper.querySelectorAll<HTMLButtonElement>(
      '.vssl-stripe--table--sort-button'
    )

    sortButtons.forEach((button) => {
      const header = button.closest<HTMLTableCellElement>('th')
      if (!header) return

      button.addEventListener('click', () => {
        const columnIndex = parseInt(header.dataset.columnIndex || '0')
        const sortMethod = (header.dataset.sortMethod ||
          'alpha-asc') as SortMethodWithDirection
        handleSort(columnIndex, sortMethod)
      })
    })

    // Create "clear all filters" button and insert it after the filters container
    const filtersContainer = wrapper.querySelector<HTMLElement>(
      '.vssl-stripe--table--filters'
    )
    const filterSelects = wrapper.querySelectorAll<HTMLSelectElement>(
      '.vssl-stripe--table--filter-select'
    )
    let clearFiltersButton: HTMLButtonElement
    if (filtersContainer) {
      clearFiltersButton = document.createElement('button')
      clearFiltersButton.className = 'vssl-stripe--table--clear-filters'
      clearFiltersButton.textContent = 'Clear all filters'
      clearFiltersButton.hidden = true
      clearFiltersButton.addEventListener('click', () => {
        for (const key in activeFilters) {
          delete activeFilters[key]
        }
        filterSelects.forEach((s) => (s.value = ''))
        applyFilters()
      })
      filtersContainer.after(clearFiltersButton)
    }

    function updateClearFiltersButton(): void {
      if (!clearFiltersButton) return
      clearFiltersButton.hidden = Object.keys(activeFilters).length === 0
    }

    // Initialize filters if any are enabled
    if (filterMethods.some((method) => method === 'enabled')) {
      initializeFilters()
    }

    // Initialize alternating row classes
    updateAlternatingClasses()
  })
}
